/* public-i18n-migrated */
import { useI18n } from "@/i18n";
import { PublicLanguageSelect } from '@/components/PublicLanguageSelect';
import { localizedBbqEvent } from '@/lib/bbqLocale';
import { ConsentHistory } from '@/booking/ConsentHistory';
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Flame, Loader2, XCircle } from "lucide-react";
import AllRounderLogo from "@/components/AllRounderLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BbqPaymentSuccessPage() {
  const { tx, lang } = useI18n();

  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [phase, setPhase] = useState("checking"); // checking | paid | timeout | error
  const [eventSource, setEvent] = useState(null);
  const [bookingIdentity,setBookingIdentity] = useState(null);
  const event = localizedBbqEvent(eventSource, lang);
  const attempts = useRef(0);

  useEffect(() => {
    document.title = "Payment · BBQ Party";
    axios.get(`${API}/bbq-event`).then((r) => setEvent(r.data)).catch(() => {});
    if (!sessionId) {
      setPhase("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if(r.data.booking_id&&r.data.event_id)setBookingIdentity({id:r.data.booking_id,event_id:r.data.event_id});
        if (r.data.payment_status === "paid") {
          sessionStorage.removeItem("bbq_pending_rsvp");
          setPhase("paid");
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setPhase("error");
          return;
        }
      } catch {
        if (attempts.current >= 3) {
          setPhase("error");
          return;
        }
      }
      if (attempts.current >= 20) {
        setPhase("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main
      data-testid="bbq-payment-success-page"
      className="relative min-h-screen bg-[#0A0806] text-white flex flex-col items-center justify-center px-6 text-center"
      style={{ fontFamily: "'Noto Sans KR', 'Fraunces', serif" }}
    >
      <div className="fixed inset-0 bg-[#0A0806] -z-20" />
      <div className="absolute top-5 right-5 z-30"><PublicLanguageSelect id="payment-language"/></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(232,90,44,0.12),transparent_60%)]" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
        className="relative z-10 max-w-lg w-full"
      >
        <div className="flex justify-center mb-8">
          <AllRounderLogo variant="mark" color="#FAFAFA" size={36} />
        </div>

        {phase === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 text-[#E85A2C] mx-auto animate-spin" />
            <h1
              className="mt-6 text-3xl sm:text-4xl font-bold tracking-tight"
              style={{ fontFamily: "'Fraunces', serif" }}
            > {tx("Confirming your payment…")} </h1>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">{tx("Please wait while we confirm your payment. Do not close this page.")}</p>
          </div>
        )}

        {phase === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h1
              className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Fraunces', serif" }}
            > {tx("You're in! 🔥")} </h1>
            <p className="mt-5 text-white/70 text-base leading-relaxed">{tx("Payment confirmed — your spot is locked. A confirmation email is on its way.")}</p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { label: "Date", value: event?.short_date || "—" },
                { label: "Time", value: event?.short_time || "—" },
                { label: "Location", value: event?.short_location || "—" },
              ].map((s) => (
                <div key={s.label} className="rounded-sm border border-white/10 bg-black/20 p-3">
                  <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/50">{tx(s.label)}</div>
                  <div className="mt-1 text-white text-sm font-bold" style={{ fontFamily: "'Fraunces', serif" }}>
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {phase === "timeout" && (
          <div data-testid="payment-timeout">
            <Flame className="w-10 h-10 text-[#E85A2C] mx-auto" />
            <h1 className="mt-6 text-3xl font-bold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}> {tx("Still confirming…")} </h1>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">{tx("Confirmation is taking longer than usual. Check your email shortly.")}</p>
          </div>
        )}

        {phase === "error" && (
          <div data-testid="payment-error">
            <XCircle className="w-10 h-10 text-red-400 mx-auto" />
            <h1 className="mt-6 text-3xl font-bold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}> {tx("Payment not confirmed")} </h1>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">{tx("We couldn't confirm this payment. Head back and try again.")}</p>
          </div>
        )}

        <Link
          to="/bbq"
          data-testid="payment-back-to-bbq"
          className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-white/25 text-white hover:border-[#E85A2C] hover:text-[#E85A2C] transition-colors text-[11px] uppercase tracking-[0.28em] font-bold"
        >
          <ArrowLeft className="w-4 h-4" /> {tx("Back to BBQ page")} </Link>
      </motion.div>
      {bookingIdentity&&<div className="relative z-10 max-w-lg w-full"><ConsentHistory id="bbq-payment-consent" scope="bbq" eventId={bookingIdentity.event_id} bookingId={bookingIdentity.id}/></div>}
    </main>
  );
}
